import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertCircle, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

var API = "/api";

export default function ResumeCartPage() {
  var [params] = useSearchParams();
  var navigate = useNavigate();
  var { addToCart, clearCart } = useCart();

  // Derive initial state from URL params so we don't call setState synchronously inside the effect.
  var initialOrderId = params.get("order");
  var [status, setStatus] = useState(initialOrderId ? "loading" : "error"); // loading | success | error
  var [errorMsg, setErrorMsg] = useState(initialOrderId ? "" : "This recovery link is missing the order ID.");
  var [itemsRestored, setItemsRestored] = useState(0);
  // Guard against React StrictMode double-invocation in dev
  var hasRunRef = useRef(false);

  useEffect(function() {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    var orderId = params.get("order");
    if (!orderId) return; // initial state already set to error

    axios.get(API + "/orders/resume-cart/" + encodeURIComponent(orderId))
      .then(function(res) {
        var items = (res.data && res.data.items) || [];
        if (!items.length) {
          setStatus("error");
          setErrorMsg("This checkout has no items left to restore.");
          return;
        }
        // Replace whatever's in the cart with the abandoned-order items
        clearCart();
        items.forEach(function(item) {
          var product = {
            id: item.product_id || ("recovered-" + Math.random().toString(36).slice(2, 8)),
            name: item.name,
            price: item.price,
            images: item.image ? [item.image] : [],
          };
          var addons = (item.addons || []).map(function(a) {
            return { id: a.id || a.name, name: a.name, price: a.price, quantity: a.quantity || 1 };
          });
          addToCart(product, item.quantity || 1, addons);
        });
        setItemsRestored(items.length);
        setStatus("success");
        toast.success("Welcome back! Your cart has been restored.");
        // Brief pause so the user sees the success state, then send them to checkout
        setTimeout(function() {
          navigate("/checkout");
        }, 1400);
      })
      .catch(function(err) {
        var msg = (err && err.response && err.response.data && err.response.data.detail)
          || "We couldn't restore this cart. Please add items again from the shop.";
        setStatus("error");
        setErrorMsg(msg);
      });
  }, [params, addToCart, clearCart, navigate]);

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="resume-cart-page">
      <div className="container-tactical max-w-lg text-center">
        {status === "loading" && (
          <div data-testid="resume-loading">
            <Loader2 className="w-12 h-12 text-[#C0A062] animate-spin mx-auto mb-6" />
            <h1 className="font-heading font-black text-3xl text-white uppercase mb-3">Restoring Your Cart…</h1>
            <p className="text-[#A1A1AA]">Hold tight — pulling your items back from where you left off.</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="resume-success">
            <ShoppingBag className="w-12 h-12 text-[#8CB369] mx-auto mb-6" />
            <h1 className="font-heading font-black text-3xl text-white uppercase mb-3">Welcome Back</h1>
            <p className="text-[#A1A1AA] mb-2">{itemsRestored} item{itemsRestored === 1 ? "" : "s"} restored to your cart.</p>
            <p className="text-[#71717a] text-sm">Taking you to checkout…</p>
          </div>
        )}

        {status === "error" && (
          <div data-testid="resume-error">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-6" />
            <h1 className="font-heading font-black text-3xl text-white uppercase mb-3">Cart Unavailable</h1>
            <p className="text-[#A1A1AA] mb-6">{errorMsg}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/shop">
                <Button className="btn-primary w-full sm:w-auto" data-testid="resume-shop-btn">Browse Shop</Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full sm:w-auto" data-testid="resume-home-btn">Back to Home</Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
